import styles from "scss/Page.module.scss";
import AddButton from "components/Shared/Button/AddButton";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { DynamicFilter } from "components/Shared/DynamicFilter";
import { useAppSelector } from "redux/store";
import { Box, Card, Grid } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useGetCompaniesQuery, useDeleteCompanyMutation } from "redux/api/companyApi";
import { FullScreenLoader, PageTitle } from "components";
import { Company } from "components/Company";
import {  removeCurrentFilters, updateFilters } from "redux/features/companySlice";
import { ICompanyState } from "redux/features/companySlice";
import { EDynamicColumnType, IDynamicColumnFilter, ISelectOption } from "Interfaces/Utils";
import { CompaniesFilters } from "Interfaces/CompaniesFilters";
import { useGetAllProvincesQuery } from "redux/api/provinceApi";
import { useIntl } from "react-intl";


/**
 * Component to render news main page
 */

export const CompanyPage = () => {
  const { formatMessage } = useIntl();

  const dispatch = useDispatch();

  const PAGE_TITLE = formatMessage({ id: "company.title" });
  const DELETE_SUCCESS_MESSAGE = formatMessage({ id: "company.deleteSuccessMessage" });
  // App state selectors

  const { filters, pageIndex, pageSize, sort } = useAppSelector((state: { companyState: ICompanyState }) =>  state.companyState);
  const {data: provinces, isLoading: isProvincesLoading} = useGetAllProvincesQuery();
  
    

  // API calls

  const { data, isLoading } = useGetCompaniesQuery({
    filters: filters,
    pageIndex: pageIndex,
    pageSize: pageSize,
    sort: sort,
  });

  // Component state

  const [deleteCompany, { isSuccess: isCompanyDeletedSuccess }] = useDeleteCompanyMutation();

  // If is correctly deleted, user is notified

  useEffect(() => {

    if (isCompanyDeletedSuccess)
      toast.success(DELETE_SUCCESS_MESSAGE)

  }, [isCompanyDeletedSuccess]);

  const provinceSelectOptions: ISelectOption[] = useMemo(() => {
      
    if (!provinces) return [];    
     return provinces.map((province) => ({
      label: province.name,
      value: province._id,
    }));
  }, [provinces])

  // Table column filters 

  const columnFilters: IDynamicColumnFilter[] = [
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "name",
      label: formatMessage({ id: "company.filters.name" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressType",
      label: formatMessage({ id: "company.filters.addressType" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressName",
      label: formatMessage({ id: "company.filters.addressName" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressNumber",
      label: formatMessage({ id: "company.filters.addressNumber" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressDetails",
      label: formatMessage({ id: "company.filters.addressDetails" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "postalCode",
      label: formatMessage({ id: "company.filters.postalCode" }),
    },
    {
      type: EDynamicColumnType.SELECT,
      grid: 6,
      field: "province",
      label: formatMessage({ id: "company.filters.province" }),
      values: provinceSelectOptions,
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "nif",
      label: formatMessage({ id: "company.filters.nif" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "locality",
      label: formatMessage({ id: "company.filters.locality" }),
    },

    {
      type: EDynamicColumnType.NUMBER,
      grid: 6,
      field: "limit",
      label: formatMessage({ id: "company.filters.limit" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "phoneNumber",
      label: formatMessage({ id: "company.filters.phoneNumber" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "email",
      label: formatMessage({ id: "company.filters.email" }),
    },
    // Rango de fechas (mínima)
    {
      type: EDynamicColumnType.DATE,
      grid: 6,
      field: "desactivationDateMin",
      label: formatMessage({ id: "company.filters.desactivationDateMin"}),
    },
    // Rango de fechas (máxima)
    {
      type: EDynamicColumnType.DATE,
      grid: 6,
      field: "desactivationDateMax",
      label: formatMessage({ id: "company.filters.desactivationDateMax"}),
    },

  ];

  // Table management

  const handleSubmit = (filters: Record<string, any>) => {
    dispatch(updateFilters(filters as CompaniesFilters));
  };

  const removeFilter = (code: string) => {
    dispatch(removeCurrentFilters(code));
  };

  const onDelete = (companyId: string) => {
    deleteCompany(companyId);
  };

  if (isLoading || isProvincesLoading) {
    return <FullScreenLoader />;
  }
  return (
    <Grid container className={styles.viewContainer}>
      <Grid item xs={12} >
        <Box display={"flex"} justifyContent={"space-between"}>
          <Box display={"flex"} alignItems={"center"} gap="20px" flexWrap={"wrap"}>
            <PageTitle pageTitle={PAGE_TITLE} />
            <DynamicFilter.AppliedFilters filters={columnFilters} filterValues={filters} handleRemoveFilter={removeFilter} />
          </Box>
          <Grid item xs={12} md={4}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  width: "480px !important",
                  position: "relative",
                }}
              >
                <DynamicFilter.Toolbar
                  filters={columnFilters}
                  externalFilterValues={filters}
                  filterData={handleSubmit}
                />

                <AddButton urlName={"/create-company"} label={formatMessage({ id: "company.add" })}/>
              </div>
            </div>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={16}>
        <Card className={styles.pageCard} sx={{ padding: "0" }}>
          <Company.Table
            deleteCompany={onDelete}
            paginatedQuery={data}
            isLoading={isLoading}
            provinces={provinceSelectOptions}
          />
        </Card>
      </Grid>
    </Grid>
  );
};
